import React from 'react';
import { Button, CircularProgress, type ButtonProps } from '@mui/material';
import { ACCOUNT_COLORS } from './accountPageStyles';

type AccountButtonVariant =
  | 'primary'
  | 'navy'
  | 'outline'
  | 'ghost'
  | 'gold'
  | 'link'
  | 'danger'
  | 'dangerOutline'
  | 'dangerGhost';

interface AccountButtonProps extends Omit<ButtonProps, 'variant' | 'color'> {
  accountVariant?: AccountButtonVariant;
  loading?: boolean;
}

const variantSx: Record<AccountButtonVariant, object> = {
  primary: {
    bgcolor: ACCOUNT_COLORS.primary,
    color: '#fff',
    border: '1px solid transparent',
    boxShadow: '0 1px 2px rgba(204, 27, 27, 0.2)',
    '&:hover': {
      bgcolor: ACCOUNT_COLORS.primaryHover,
      boxShadow: '0 4px 12px rgba(204, 27, 27, 0.25)',
    },
    '&:disabled': { bgcolor: '#E8EAED', color: '#9AA0A6', boxShadow: 'none' },
  },
  navy: {
    bgcolor: ACCOUNT_COLORS.navy,
    color: '#fff',
    border: '1px solid transparent',
    boxShadow: '0 1px 2px rgba(35, 47, 62, 0.2)',
    '&:hover': {
      bgcolor: ACCOUNT_COLORS.navyHover,
      boxShadow: '0 4px 12px rgba(35, 47, 62, 0.22)',
    },
    '&:disabled': { bgcolor: '#E8EAED', color: '#9AA0A6', boxShadow: 'none' },
  },
  outline: {
    bgcolor: '#fff',
    color: ACCOUNT_COLORS.text,
    border: `1px solid ${ACCOUNT_COLORS.border}`,
    boxShadow: 'none',
    '&:hover': {
      bgcolor: ACCOUNT_COLORS.surfaceMuted,
      borderColor: ACCOUNT_COLORS.borderStrong,
    },
  },
  ghost: {
    bgcolor: 'transparent',
    color: ACCOUNT_COLORS.textMuted,
    border: '1px solid transparent',
    boxShadow: 'none',
    '&:hover': { bgcolor: ACCOUNT_COLORS.surfaceMuted, color: ACCOUNT_COLORS.text },
  },
  gold: {
    background: ACCOUNT_COLORS.walletAccent,
    color: '#fff',
    border: '1px solid transparent',
    boxShadow: '0 1px 2px rgba(204, 27, 27, 0.2)',
    '&:hover:not(.Mui-disabled)': {
      filter: 'brightness(1.06)',
      color: '#fff',
      boxShadow: '0 4px 12px rgba(204, 27, 27, 0.25)',
    },
    '&.Mui-disabled': {
      background: '#E8EAED',
      backgroundImage: 'none',
      color: '#9AA0A6',
      filter: 'none',
      boxShadow: 'none',
      opacity: 1,
    },
  },
  link: {
    bgcolor: 'transparent',
    color: ACCOUNT_COLORS.textLo,
    border: `1px dashed ${ACCOUNT_COLORS.border}`,
    boxShadow: 'none',
    '&:hover': {
      borderColor: ACCOUNT_COLORS.gold,
      color: ACCOUNT_COLORS.goldBright,
      borderStyle: 'solid',
      bgcolor: 'transparent',
    },
  },
  danger: {
    bgcolor: ACCOUNT_COLORS.danger,
    color: '#fff',
    border: '1px solid transparent',
    '&:hover': { bgcolor: ACCOUNT_COLORS.dangerHover },
  },
  dangerOutline: {
    bgcolor: '#fff',
    color: ACCOUNT_COLORS.danger,
    border: `1px solid ${ACCOUNT_COLORS.dangerBorder}`,
    boxShadow: 'none',
    '&:hover': { bgcolor: ACCOUNT_COLORS.dangerBg, borderColor: ACCOUNT_COLORS.danger },
  },
  dangerGhost: {
    bgcolor: 'transparent',
    color: ACCOUNT_COLORS.danger,
    border: `1px solid ${ACCOUNT_COLORS.borderSoft}`,
    boxShadow: 'none',
    '&:hover': { borderColor: ACCOUNT_COLORS.danger, bgcolor: ACCOUNT_COLORS.dangerBg },
  },
};

export const AccountButton: React.FC<AccountButtonProps> = ({
  accountVariant = 'primary',
  loading = false,
  disabled,
  children,
  sx,
  ...props
}) => (
  <Button
    {...props}
    disabled={disabled || loading}
    sx={{
      minHeight: 44,
      py: 1.25,
      px: 2.5,
      borderRadius: '10px',
      fontWeight: 600,
      fontSize: '0.875rem',
      fontFamily: ACCOUNT_COLORS.fontBody,
      textTransform: 'none',
      letterSpacing: '-0.01em',
      transition: 'background-color 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
      ...variantSx[accountVariant],
      ...sx,
    }}
  >
    {loading ? <CircularProgress size={20} color="inherit" /> : children}
  </Button>
);
